import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi } from '../../app/providers';
import { href, Link, navigate } from '../../app/router';
import type { Org } from '../../lib/api';
import { Button, DisplayTitle, Menu } from '../../ui';
import { BoardGrid } from './BoardGrid';
import { Grid, ProjectCard, Section } from './cards';
import { SelectionScope } from './SelectionScope';
import { ServiceUnavailable } from './ServiceUnavailable';
import { ApiError } from '../../lib/api';
import { Breadcrumb } from './Breadcrumb';
import { ListingBar, useListingPrefs } from './ListingBar';
import { DEFAULT_FILTERS, listBoards } from './model/listing';
import { useSession } from '../auth/session';
import { useFolderDrag } from './dnd/useDashboardDrag';
import type { Folder } from '../../lib/api';
import { NameDialog } from './dialogs';
import { FolderActions, ProjectActions } from './ProjectActions';
import { useBoardImport } from './ImportBoardButton';
import { menuTriggerClass } from './ItemMenu';
import { Plus } from 'lucide-react';
import { Icon } from '../../ui/icons';
import { NewBoardDialog } from '../templates/NewBoardDialog';
import { usePaletteCommands } from '../palette/registry';
import {
  useBoards,
  useFavorites,
  useFolders,
  useInbox,
  useInvalidateContent,
  useOrg,
  useProject,
  useProjects,
  useRecents,
  useSearch,
} from './queries';
import { PageSkeleton } from '../../ui/states';

export function HomePage({ org }: { org: Org }) {
  const { t } = useTranslation();
  const api = useApi();
  const invalidate = useInvalidateContent();
  const recents = useRecents();
  const projects = useProjects(org.id);
  const inbox = useInbox(org.id);
  const looseBoards = useBoards(inbox.data ? { orgId: org.id, projectId: inbox.data } : null);
  const session = useSession();
  const { i18n } = useTranslation();
  const [creating, setCreating] = useState<'project' | 'board' | null>(null);
  // « Sans projet » (P17): every loose board, the last edited first; archived ones show with their tag, as in Recent.
  const loose = listBoards(looseBoards.data ?? [], {
    sort: 'edited',
    filters: { ...DEFAULT_FILTERS, archived: true },
    userId: session.status === 'signed-in' ? session.user.id : '',
    lng: i18n.language,
  });
  const newLoose = <Button onClick={() => setCreating('board')}>{t('dashboard.newBoard')}</Button>;

  return (
    <SelectionScope>
      <DisplayTitle as="h1" text={t('dashboard.heroTitle')} />
      {(projects.isError || recents.isError || inbox.isError) && (
        <ServiceUnavailable onRetry={() => void Promise.all([projects.refetch(), recents.refetch(), inbox.refetch()])} />
      )}
      <Section title={t('dashboard.recent')} empty={recents.data?.length === 0} emptyBody={t('dashboard.emptyBody.recent')}>
        <BoardGrid boards={recents.data?.map((r) => r.board) ?? []} />
      </Section>
      <Section
        title={t('dashboard.projects')}
        empty={projects.data?.length === 0}
        emptyBody={t('dashboard.emptyBody.projects')}
        action={
          <Button variant="primary" onClick={() => setCreating('project')}>
            {t('dashboard.newProject')}
          </Button>
        }
      >
        <Grid>
          {projects.data?.map((p) => (
            <ProjectCard key={p.id} project={p} />
          ))}
        </Grid>
      </Section>
      {inbox.data && (
        <Section
          title={t('dashboard.noProject')}
          empty={looseBoards.isSuccess && loose.length === 0}
          emptyBody={t('dashboard.emptyBody.noProject')}
          action={newLoose}
          emptyAction={newLoose}
          drop={{ projectId: inbox.data, folderId: null, archived: false }}
        >
          <BoardGrid boards={loose} />
        </Section>
      )}
      {creating === 'board' && inbox.data && <NewBoardDialog org={org} projectId={inbox.data} folderId={null} onClose={() => setCreating(null)} />}
      {creating === 'project' && (
        <NameDialog
          title={t('dashboard.newProject')}
          label={t('dashboard.projectName')}
          submitLabel={t('dashboard.create')}
          max={120}
          onClose={() => setCreating(null)}
          onSubmit={async (name) => {
            const project = await api.createProject({ orgId: org.id, name });
            await invalidate();
            navigate(href('project', { projectId: project.id }));
          }}
        />
      )}
    </SelectionScope>
  );
}

/** A folder's section: boards drop into it, its heading drags it to another place in the project's order. */
function FolderSection({
  folder,
  writable,
  ...rest
}: { folder: Folder; writable: boolean } & Omit<
  Parameters<typeof Section>[0],
  'title' | 'headingProps'
>) {
  const drag = useFolderDrag(folder, writable);
  return <Section title={folder.name} headingProps={writable ? drag : undefined} {...rest} />;
}

export function ProjectPage({ projectId, folderId }: { projectId: string; folderId?: string }) {
  const { t, i18n } = useTranslation();
  const session = useSession();
  const [prefs, setPrefs] = useListingPrefs('project');
  const api = useApi();
  const invalidate = useInvalidateContent();
  const { org } = useOrg();
  const project = useProject(projectId);
  const folders = useFolders(projectId);
  const boards = useBoards(project.data ? { orgId: project.data.orgId, projectId } : null);
  const importer = useBoardImport(project.data?.orgId ?? '', projectId);
  const [dialog, setDialog] = useState<
    { kind: 'board'; folderId: string | null } | { kind: 'folder' } | null
  >(null);
  usePaletteCommands(() => {
    const p = project.data;
    const writable = p && org && org.id === p.orgId && !p.archivedAt && !p.deletedAt;
    return writable
      ? [
          {
            id: 'new-board',
            label: t('palette.newBoard'),
            group: 'board',
            keywords: ['new', 'nouveau', 'create', 'créer'],
            run: () => setDialog({ kind: 'board', folderId: null }),
          },
        ]
      : [];
  });

  // A missing or forbidden project is "not found"; a server or network failure is an outage, with a retry (P12a).
  const missing = project.error instanceof ApiError && (project.error.code === 'not_found' || project.error.code === 'forbidden');
  if (project.isError && missing) return <p className="font-sans text-fg-muted">{t('app.notFound')}</p>;
  if (project.isError && !project.data) return <ServiceUnavailable onRetry={() => void project.refetch()} />;
  if (!project.data) return <PageSkeleton label={t('auth.loading')} />;
  const p = project.data;
  // « Sans projet » opened by its address (P17): a page of loose boards, never a project (no folders, no project menu).
  const loose = p.isInbox;
  const userId = session.status === 'signed-in' ? session.user.id : '';
  const all = listBoards(boards.data ?? [], {
    sort: prefs.sort,
    filters: prefs.filters,
    userId,
    lng: i18n.language,
  });
  const root = all.filter((b) => !b.folderId || !folders.data?.some((f) => f.id === b.folderId));
  const folder = folderId ? folders.data?.find((f) => f.id === folderId) : undefined;
  const shownFolders = loose ? [] : folderId ? (folder ? [folder] : []) : (folders.data ?? []);
  // Externals with a project grant (no organization) only open its boards (spec §9.2: canvas only).
  const member = org && org.id === p.orgId ? org : null;
  const writable = Boolean(member) && !p.archivedAt && !p.deletedAt;
  const newBoard = (folderId: string | null) =>
    writable ? (
      <Button onClick={() => setDialog({ kind: 'board', folderId })}>
        {t('dashboard.newBoard')}
      </Button>
    ) : undefined;
  const cards = (list: typeof all) => <BoardGrid boards={list} view={prefs.view} />;
  const home = member
    ? { label: t('dashboard.home'), to: href('home') }
    : { label: t('dashboard.shared'), to: href('shared') };
  const crumbs = loose
    ? [home, { label: t('dashboard.noProject') }]
    : folder
      ? [home, { label: p.name, to: href('project', { projectId: p.id }) }, { label: folder.name }]
      : [home, { label: p.name }];

  return (
    <SelectionScope>
      {(boards.isError || folders.isError) && <ServiceUnavailable onRetry={() => void Promise.all([boards.refetch(), folders.refetch()])} />}
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-col gap-1">
          <Breadcrumb items={crumbs} />
          <h1 className="font-display text-display-md font-normal tracking-display text-fg">
            {loose ? t('dashboard.noProject') : folder ? folder.name : p.name}
          </h1>
          {!folder && !loose && p.clientName && (
            <p className="font-mono text-sm text-fg-muted">{p.clientName}</p>
          )}
        </div>
        <div className="flex flex-col items-end gap-1">
          <div className="flex items-start gap-2">
            {writable && (
              <Button
                variant="primary"
                onClick={() => setDialog({ kind: 'board', folderId: folderId ?? null })}
              >
                {t('dashboard.newBoard')}
              </Button>
            )}
            {writable && (!loose || importer.available) && (
              <Menu.Root>
                <Menu.Trigger aria-label={t('dashboard.moreToCreate')} className={menuTriggerClass}>
                  <Icon icon={Plus} size={18} />
                </Menu.Trigger>
                <Menu.Popup label={t('dashboard.moreToCreate')} align="end">
                  {!loose && (
                    <Menu.Item onClick={() => setDialog({ kind: 'folder' })}>
                      {t('dashboard.newFolder')}
                    </Menu.Item>
                  )}
                  {importer.available && (
                    <Menu.Item onClick={importer.pick}>{t('boardImport.button')}</Menu.Item>
                  )}
                </Menu.Popup>
              </Menu.Root>
            )}
            {member && !p.deletedAt && !loose && <ProjectActions project={p} />}
          </div>
          {importer.ui}
        </div>
      </header>
      {p.archivedAt && (
        <p className="font-mono text-sm text-fg-muted">{t('dashboard.projectArchived')}</p>
      )}
      <ListingBar prefs={prefs} onChange={setPrefs} mine={Boolean(member)} />
      {shownFolders.map((f) => {
        const inFolder = all.filter((b) => b.folderId === f.id);
        return (
          <FolderSection
            key={f.id}
            folder={f}
            writable={writable}
            drop={{ projectId: p.id, folderId: f.id, archived: Boolean(p.archivedAt) }}
            empty={inFolder.length === 0}
            emptyBody={t('dashboard.emptyBody.folder')}
            action={
              writable ? (
                <div className="flex items-start gap-2">
                  {newBoard(f.id)}
                  <FolderActions folder={f} />
                </div>
              ) : undefined
            }
          >
            {cards(inFolder)}
          </FolderSection>
        );
      })}
      {!folderId && (
        <Section
          title={t('dashboard.boards')}
          empty={boards.isSuccess && root.length === 0}
          emptyTitle={t('dashboard.noBoards')}
          emptyBody={t('dashboard.emptyBody.boards')}
          emptyAction={newBoard(null)}
          drop={
            member
              ? { projectId: p.id, folderId: null, archived: Boolean(p.archivedAt) }
              : undefined
          }
        >
          {cards(root)}
        </Section>
      )}
      {dialog?.kind === 'board' && member && (
        <NewBoardDialog
          org={member}
          projectId={p.id}
          folderId={dialog.folderId}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'folder' && (
        <NameDialog
          title={t('dashboard.newFolder')}
          label={t('dashboard.folderName')}
          submitLabel={t('dashboard.create')}
          max={120}
          onClose={() => setDialog(null)}
          onSubmit={async (name) => {
            await api.createFolder({ orgId: p.orgId, projectId: p.id, name });
            await invalidate();
          }}
        />
      )}
    </SelectionScope>
  );
}

/** A page of boards from everywhere (favorites, recents) with its own view, sort and filters. */
function BoardListPage({
  page,
  title,
  boards,
  empty,
  emptyBody,
}: {
  page: string;
  title: string;
  boards: ReturnType<typeof useFavorites>['data'];
  empty: boolean;
  emptyBody: string;
}) {
  const { i18n } = useTranslation();
  const session = useSession();
  const [prefs, setPrefs] = useListingPrefs(page);
  const userId = session.status === 'signed-in' ? session.user.id : '';
  const shown = listBoards(boards ?? [], {
    sort: prefs.sort,
    filters: prefs.filters,
    userId,
    lng: i18n.language,
  });
  return (
    <SelectionScope>
      <h1 className="font-display text-display-md font-normal tracking-display text-fg">{title}</h1>
      <ListingBar prefs={prefs} onChange={setPrefs} />
      <Section title={title} empty={empty} emptyBody={emptyBody}>
        <BoardGrid boards={shown} view={prefs.view} />
      </Section>
    </SelectionScope>
  );
}

export function FavoritesPage() {
  const { t } = useTranslation();
  const favorites = useFavorites();
  return (
    <BoardListPage
      page="favorites"
      title={t('dashboard.favorites')}
      boards={favorites.data}
      empty={favorites.data?.length === 0}
      emptyBody={t('dashboard.emptyBody.favorites')}
    />
  );
}

/** The 50 boards opened last (P16-4 Ruling 7). */
export function RecentPage() {
  const { t } = useTranslation();
  const recents = useRecents(50);
  return (
    <BoardListPage
      page="recent"
      title={t('dashboard.recent')}
      boards={recents.data?.map((r) => r.board)}
      empty={recents.data?.length === 0}
      emptyBody={t('dashboard.emptyBody.recent')}
    />
  );
}

export function SearchPage({ q }: { q: string }) {
  const { t } = useTranslation();
  const results = useSearch(q);
  const { org } = useOrg();
  const inbox = useInbox(org?.id).data;
  // « Sans projet » is not a project (P17): no project hit for it, and its boards say where they are in words.
  const hits = (results.data ?? []).filter((h) => !(h.kind === 'project' && h.id === inbox));
  return (
    <Section title={t('dashboard.resultsFor', { q })} empty={results.data !== undefined && hits.length === 0} emptyTitle={t('dashboard.noResults', { q })} emptyBody={t('dashboard.emptyBody.search')}>
      <ul className="flex flex-col gap-2">
        {hits.map((hit) => (
          <li key={`${hit.kind}:${hit.id}`}>
            <Link
              to={
                hit.kind === 'project'
                  ? href('project', { projectId: hit.id })
                  : href('board', { boardId: hit.id })
              }
              className="flex flex-col rounded-md border border-border px-3 py-2 hover:border-accent"
            >
              <span className="font-display font-bold text-fg">{hit.title}</span>
              <span className="font-mono text-xs text-fg-muted">
                {t(`dashboard.kind.${hit.kind}`)}
                {hit.kind !== 'project' ? ` · ${hit.projectId === inbox ? t('dashboard.noProject') : hit.projectName}` : ''}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </Section>
  );
}
